import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded"
import HourglassTopRoundedIcon from "@mui/icons-material/HourglassTopRounded"
import RuleRoundedIcon from "@mui/icons-material/RuleRounded"
import { Stack, Typography } from "@mui/material"
import { ReactElement, useState } from "react"

import DashboardPanelFrame from "../../components/dashboard/dashboardPanelFrame"
import ListDevices from "../../components/dashboard/listDevices"
import SingleKpiCard from "../../components/dashboard/singleKpiCard"
import ErrorDisplay from "../../components/fallback/errorDisplay"
import DashboardHomepageSkeleton from "../../components/loading/skeletons/dashboardHomepageSkeleton"
import ReleaseSnackbar from "../../components/snackbar/releaseSnackbar"
import { DB_ASSESSOR_GEN, DISPLAY_ROLES } from "../../constants/roles"
import {
    TT_DEVICES_ALERT,
    TT_DEVICES_AWAITING_ASSESSMENT,
} from "../../constants/tooltips"
import { useGetUserData } from "../../hooks/useGetUserData"
import {
    AWAITING_ASSESSMENT_DEVICES,
    NON_COMPLIANT_DEVICES,
} from "../../router/routes"
import { useGetValidatorHomepageData } from "../../services/middleware/hooks/validatorInterface/useGetValidatorHomepageData"
import { GRID_SPACING } from "../../themes/defaultThemeParams"

/**
 * Renders the dashboard for Generation Assessor users which is displayed as their homepage
 *
 */
const GenerationAssessorHomepage = (): ReactElement => {
    const { firstName, isV2gAssessor } = useGetUserData()
    const { data, error, isLoading } = useGetValidatorHomepageData(
        isV2gAssessor ? "V2G" : "GEN"
    )
    const [open, setOpen] = useState(false)

    if (isLoading) {
        return <DashboardHomepageSkeleton />
    }
    if (error) {
        return <ErrorDisplay error={error} />
    }
    if (!data) {
        return <Typography> KPI Currently Unavailable</Typography>
    }

    return (
        <Stack
            direction={{ xs: "column", md: "column" }}
            spacing={GRID_SPACING}
            alignItems="stretch"
        >
            <Stack>
                <DashboardPanelFrame
                    userName={firstName}
                    permission={DISPLAY_ROLES[DB_ASSESSOR_GEN]}
                />
            </Stack>

            <Stack
                direction={{ xs: "column", md: "row" }}
                gap={GRID_SPACING}
                justifyContent="center"
                marginBottom={GRID_SPACING}
            >
                <SingleKpiCard
                    Icon={HourglassTopRoundedIcon}
                    value={data.totDevicesAwaitingAssessment}
                    title="Devices Awaiting Assessment"
                    link={AWAITING_ASSESSMENT_DEVICES}
                />
                <SingleKpiCard
                    Icon={RuleRoundedIcon}
                    value={data.totDevicesNonCompliant}
                    title="Non-Compliant Devices"
                    link={NON_COMPLIANT_DEVICES}
                />
                <SingleKpiCard
                    Icon={ErrorRoundedIcon}
                    value={data.totLiveAlerts}
                    title="Devices with Alerts"
                />
            </Stack>

            <Stack
                direction={{ xs: "column", md: "row" }}
                gap={GRID_SPACING}
                justifyContent="center"
                marginBottom={GRID_SPACING}
            >
                <ListDevices
                    cardTitle="Devices Awaiting Assessment"
                    cardDescription="These are the last 20 devices that are awaiting assessment"
                    cardTooltip={TT_DEVICES_AWAITING_ASSESSMENT}
                    data={data.topAwaitingAssessmentSummary}
                />

                <ListDevices
                    cardTitle="Devices with Alerts"
                    cardDescription="These are the last 20 devices that have had alerts raised"
                    cardTooltip={TT_DEVICES_ALERT}
                    data={data.topLiveAlerts.map((alert) => ({
                        ...alert,
                        systemReference: alert.deviceSystemRef,
                    }))}
                />
            </Stack>
            <ReleaseSnackbar open={open} setOpen={setOpen} />
        </Stack>
    )
}

export default GenerationAssessorHomepage
